import * as Sentry from '@sentry/browser';

import {
  ServicePoint,
  GetUserContactDataResponse,
  GetNearByServicePointsResponse,
  OrderPreviewPriceResponseV4,
  GetServicePointResponse,
  PostUserAddressResponse,
  AddressResource,
  AtlasSDK,
  PostContractResponse,
  PostOrderResponse,
  PostContractValidationErrors,
  PostOrderConflictError,
  GetOfferShipmentMethodResponse,
  KycConfirmationResponse,
  BillingAddressResource,
  GetSellerResponseV1,
  PostOrderMarketpayRequestV3,
} from '@olxeu-a2b/core/typings';

import {
  sdkFactory,
  atlasSDKFactory,
  withTimeout,
  connectionQuality,
  getConfig,
  clearAtlasSDKInstance,
  fetchUnifiedPlatformAPI,
  UnifiedPlatformEndpoints,
  UnifiedPlatformEndpointsResponses,
} from '@olxeu-a2b/delivery-utils/src/extras';
import { AdPhoto, AtlasCountry, Countries } from '@olxeu-a2b/core/typings/delivery';
import DeliverySDK from '@olxeu-a2b/core/src/api/DeliverySDK';
import { PostingMethodTypes } from '../../routes/order-confirmation/store/postingMethodsStore';
import {
  FormInputPayload,
  AddressState,
  AtlasAdResponse,
  OrderStore,
  ServicePointState,
  Country,
  PayUDetailsState,
  PriceComponent,
  InvoiceFor,
} from './order-store';

import {
  SET_ADDRESS_VALUE,
  SET_LOADING,
  SET_CONFIRMING,
  USER_CONTACT_LOAD,
  USER_CONTACT_SUCCESS,
  USER_CONTACT_ERROR,
  PREPOPULATED_DATA_LOAD,
  PREPOPULATED_DATA_SUCCESS,
  PREPOPULATED_DATA_ERROR,
  SERVICE_POINTS_SUCCESS,
  SERVICE_POINTS_LOAD,
  SERVICE_POINTS_ERROR,
  CURRENT_SERVICE_POINTS_SELECT,
  NEARBY_SERVICE_POINTS_SUCCESS,
  NEARBY_SERVICE_POINTS_ERROR,
  POST_CONTRACT_SUCCESS,
  POST_CONTRACT_LOAD,
  POST_ORDER_SUCCESS,
  POST_ORDER_ERROR,
  POST_ORDER_LOAD,
  SERVICE_POINT_SELECT,
  SERVICE_POINT_LOAD,
  SERVICE_POINT_SUCCESS,
  SERVICE_POINT_ERROR,
  SET_DIRTY,
  CHECK_VALIDATION,
  PRICE_PREVIEW_LOAD,
  PRICE_PREVIEW_SUCCESS,
  PRICE_PREVIEW_ERROR,
  ATLAS_AD_LOAD,
  ATLAS_AD_SUCCESS,
  ATLAS_AD_ERROR,
  SET_OFFER,
  POST_CONTRACT_ERROR_,
  POST_BILLING_ERROR_,
  CLEAR_ORDER_ERRORS,
  OPERATORS_SUCCESS,
  POST_ORDER_ERROR_,
  SHIPMENT_METHODS_SUCCESS,
  SHIPMENT_METHODS_LOAD,
  DISPLAY_PREPOPULATED_DATA,
  CLEAR_PREPOPULATED_DATA,
  SET_PREPOPULATED_DATA_CONSENT_CHECKED,
  SET_CURRENT_OPERATOR,
  SET_DONATION_PRICE,
  SET_INITIAL_ADDRESS_VALUE,
  SET_SHOULD_SAVE_DATA,
  SET_REQUEST_INVOICE,
  SET_REQUEST_INVOICE_VALUE,
  SET_ADDRESS_DATA_TO_REQUEST_INVOICE_FORM,
  TOGGLE_USE_DELIVERY_DETAILS_FOR_INVOICE_FORM,
  UNSET_USE_DELIVERY_DETAILS_FOR_INVOICE_FORM,
  SET_REQUEST_INVOICE_TYPE,
  SET_PAYU_DETAILS_VALUE,
  ATLAS_COUNTRIES_LOAD,
  ATLAS_COUNTRIES_SUCCESS,
  ATLAS_COUNTRIES_ERROR,
  SET_PREPOPULATED_DATA_IS_LEGACY,
  PREFILL_PREPOPULATED_DATA,
  GET_SELLER_DETAILS,
  LOADING_SELLER_DETAILS,
  SET_KEEP_OLX_DELIVERY,
  LOYALTY_POINTS_OPT_IN_STATUS_LOAD,
  LOYALTY_POINTS_OPT_IN_STATUS_SUCCESS,
  LOYALTY_POINTS_OPT_IN_STATUS_ERROR,
  LOYALTY_POINTS_OPT_IN_CHECK_TOGGLE,
  LOYALTY_POINTS_PREVIEW_POINTS_LOAD,
  LOYALTY_POINTS_PREVIEW_POINTS_SUCCESS,
  LOYALTY_POINTS_PREVIEW_POINTS_ERROR,
  LOYALTY_POINTS_FLAG_HUB_VISIBLE_LOAD,
  LOYALTY_POINTS_FLAG_HUB_VISIBLE_SUCCESS,
  LOYALTY_POINTS_FLAG_HUB_VISIBLE_ERROR,
  LOYALTY_POINTS_WHITELIST_USER_LOAD,
  LOYALTY_POINTS_WHITELIST_USER_SUCCESS,
  LOYALTY_POINTS_WHITELIST_USER_ERROR,
} from './actions';

import {
  APIError,
  Dispatcher,
  APIHTTPError,
  ErrorReason,
  DonationPriceCodes,
} from '../../../typings';

import { fieldsValidation, isInvoiceValid } from './utils';

import {
  CurrentServicePointOperator,
  SERVICE_POINT_OPERATOR,
  USER_ROLE,
  USER_TYPE,
  API_ERROR_CODES,
} from '../../constants';

const {
  sites,
  deliveryPaths,
  country: { phonePrefix },
} = getConfig();
const deferredSDKFactory = () => Promise.resolve(sdkFactory());

export const setAddressValue =
  (dispatch: Dispatcher<FormInputPayload>) =>
    (
      input: FormInputPayload['input'],
      value: FormInputPayload['value'],
      valid: FormInputPayload['valid'],
    ) => {
      const payload: FormInputPayload = { input, value, valid };
      dispatch({ type: SET_ADDRESS_VALUE, payload });
    };

export const setPayuDetailsValues =
  (dispatch: Dispatcher<FormInputPayload>) =>
    (
      input: FormInputPayload['input'],
      value: FormInputPayload['value'],
      valid: FormInputPayload['valid'],
    ) => {
      const payload: FormInputPayload = { input, value, valid };
      dispatch({ type: SET_PAYU_DETAILS_VALUE, payload });
    };

export const setInitialAddressValue =
  (dispatch: Dispatcher<AddressState>) => (payload: AddressState) =>
    dispatch({ type: SET_INITIAL_ADDRESS_VALUE, payload });

export const setInvoice =
  (dispatch: Dispatcher<boolean | FormInputPayload>) =>
    (payload: boolean, orderStore: OrderStore, prefilledData?: Array<FormInputPayload>) => {
      dispatch({ type: SET_REQUEST_INVOICE, payload });

      if (prefilledData) {
        prefilledData.forEach(({ input, value, valid: validItem }) => {
          if (value) {
            dispatch({
              type: SET_REQUEST_INVOICE_VALUE,
              payload: { input, value, valid: validItem },
            });
          }
        });
      }

      const { address, currentOperator, invoice, invoiceType, valid } = orderStore;

      // When Request Invoice is opt out and form is invalid, Address form should be revalidated
      if (!payload && !valid) {
        dispatch({
          type: CHECK_VALIDATION,
          payload: fieldsValidation(address, currentOperator),
        });
      }

      // When Request Invoice is opt in and Address form is valid, Request Invoice form should be validated
      if (payload && valid) {
        dispatch({
          type: CHECK_VALIDATION,
          payload: isInvoiceValid(invoice, invoiceType || USER_TYPE.BUSINESS),
        });
      }
    };

export const setInvoiceType =
  (dispatch: Dispatcher<string | boolean>) => (payload: string, orderStore: OrderStore) => {
    dispatch({ type: SET_REQUEST_INVOICE_TYPE, payload });
    const { invoice, valid } = orderStore;
    if (payload && valid) {
      dispatch({
        type: CHECK_VALIDATION,
        payload: isInvoiceValid(invoice, payload),
      });
    }
  };

export const setInvoiceValue =
  (dispatch: Dispatcher<FormInputPayload>) =>
    (
      input: FormInputPayload['input'],
      value: FormInputPayload['value'],
      valid: FormInputPayload['valid'],
    ) =>
      dispatch({
        type: SET_REQUEST_INVOICE_VALUE,
        payload: { input, value, valid },
      });

export const setShouldSaveData = (dispatch: Dispatcher<boolean>) => (payload: boolean) =>
  dispatch({ type: SET_SHOULD_SAVE_DATA, payload });

export const setCurrentOperator =
  (dispatch: Dispatcher<ValueOf<CurrentServicePointOperator>>) =>
    (operator: ValueOf<CurrentServicePointOperator>) =>
      dispatch({ type: SET_CURRENT_OPERATOR, payload: operator });

export const setCurrentDonation =
  (dispatch: Dispatcher<DonationPriceCodes>) => (donation: DonationPriceCodes) =>
    dispatch({ type: SET_DONATION_PRICE, payload: donation });

export const setServicePoint =
  (dispatch: Dispatcher<ServicePoint>) => (servicePoint: ServicePoint) =>
    dispatch({ type: SERVICE_POINT_SELECT, payload: servicePoint });

export const setCurrentServicePoint =
  (dispatch: Dispatcher<ServicePoint>) => (servicePoint: ServicePoint) =>
    dispatch({ type: CURRENT_SERVICE_POINTS_SELECT, payload: servicePoint });

// TODO: DO-3088 Clean Up - Legacy Prefill Banner
export const displayPrepopulatedData =
  (dispatch: Dispatcher<ServicePoint | number>) => (servicePoint: ServicePoint) => {
    dispatch({ type: DISPLAY_PREPOPULATED_DATA, payload: servicePoint });
    dispatch({ type: SET_DIRTY, payload: new Date().getTime() });
  };

export const prefillPrepopulatedData = (dispatch: Dispatcher<number>) => () => {
  dispatch({ type: PREFILL_PREPOPULATED_DATA });
  dispatch({ type: SET_DIRTY, payload: new Date().getTime() });
};

export const clearPrepopulatedData = (dispatch: Dispatcher<void>) => () => {
  dispatch({ type: CLEAR_PREPOPULATED_DATA });
};

export const setLoading = (dispatch: Dispatcher<boolean>) => (loading: boolean) =>
  dispatch({ type: SET_LOADING, payload: loading });

export const clearOrderErrors = (dispatch: Dispatcher<void>) => () =>
  dispatch({ type: CLEAR_ORDER_ERRORS });

export const checkValidation =
  (dispatch: Dispatcher<boolean>) =>
    (
      { servicePoint, address, currentOperator, isInvoiceOptIn, invoice, invoiceType }: OrderStore,
      checkBankDetails?: boolean,
    ) => {
      if (servicePoint === null) {
        dispatch({ type: CHECK_VALIDATION, payload: false });
        return false;
      }

      if (checkBankDetails && !address.bank) {
        dispatch({
          type: CHECK_VALIDATION,
          payload: false,
        });

        return false;
      }

      const values = Object.values(address);
      if (values.length === 0) {
        dispatch({ type: CHECK_VALIDATION, payload: false });

        return false;
      }

      if (isInvoiceOptIn && invoiceType) {
        dispatch({
          type: CHECK_VALIDATION,
          payload: fieldsValidation(address, currentOperator) && isInvoiceValid(invoice, invoiceType),
        });

        return fieldsValidation(address, currentOperator) && isInvoiceValid(invoice, invoiceType);
      }

      dispatch({
        type: CHECK_VALIDATION,
        payload: fieldsValidation(address, currentOperator),
      });

      return fieldsValidation(address, currentOperator);
    };

export const checkPayuFormValidation =
  (dispatch: Dispatcher<boolean>) =>
    ({
      sellerType,
      payuDetails,
    }: {
    sellerType: NonNullable<KycConfirmationResponse['data']>['sellerType'];
    payuDetails: PayUDetailsState;
  }) => {
      if (sellerType === 'PRIVATE') {
        dispatch({
          type: CHECK_VALIDATION,
          payload: !!payuDetails?.birthDate?.valid && !!payuDetails?.iban?.valid,
        });

        return !!payuDetails?.birthDate?.valid && !!payuDetails?.iban?.valid;
      }

      if (sellerType === 'BUSINESS') {
        dispatch({
          type: CHECK_VALIDATION,
          payload: !!payuDetails?.iban?.valid,
        });

        return !!payuDetails?.iban?.valid;
      }

      return true;
    };

export const forceValidation =
  (dispatch: Dispatcher<boolean | number>) =>
    (orderStore: OrderStore, checkBankDetails?: boolean) => {
      dispatch({ type: SET_DIRTY, payload: new Date().getTime() });
      return checkValidation(dispatch)(orderStore, checkBankDetails);
    };

export const setConfirming = (dispatch: Dispatcher<boolean>) => (confirming: boolean) =>
  dispatch({ type: SET_CONFIRMING, payload: confirming });

export const setKeepOlxDelivery = (dispatch: Dispatcher<boolean>) => (keepOlxDelivery: boolean) =>
  dispatch({ type: SET_KEEP_OLX_DELIVERY, payload: keepOlxDelivery });

export const setMyDeliveryDetailsForInvoice =
  (dispatch: Dispatcher<Record<string, FormInputPayload> | boolean>) =>
    (addFields: FormInputPayload[], orderStore: OrderStore) => {
      const payload = addFields.reduce(
        (acc, field: FormInputPayload) => ({
          ...acc,
          [field.input]: field,
        }),
        {},
      );
      dispatch({ type: SET_ADDRESS_DATA_TO_REQUEST_INVOICE_FORM, payload });
      const updatedState = {
        ...orderStore,
        invoice: {
          ...orderStore.invoice,
          ...payload,
        },
      };
      return checkValidation(dispatch)(updatedState);
    };

export const toggleUseDeliveryDetailsForInvoice =
  (dispatch: Dispatcher<boolean>) => (isChecked: boolean) =>
    dispatch({
      type: TOGGLE_USE_DELIVERY_DETAILS_FOR_INVOICE_FORM,
      payload: isChecked,
    });

export const unsetUseDeliveryDetailsForInvoice = (dispatch: Dispatcher<boolean>) => () =>
  dispatch({ type: UNSET_USE_DELIVERY_DETAILS_FOR_INVOICE_FORM });

const apiError: APIError = (dispatch, type) => (err: APIHTTPError | Error) => {
  dispatch({ type, payload: err });
  if (!('status' in err) || err.status !== 401) {
    Sentry.captureException(err);
  }
};

const apiSuccess =
  <T>(dispatch: Dispatcher<T>, type: string) =>
    (res: T) =>
      dispatch({ type, payload: res });

export const getContactData =
  (dispatch: Dispatcher<GetUserContactDataResponse | APIHTTPError | Error>) => () =>
    deferredSDKFactory()
      .then(sdk => {
        dispatch({ type: USER_CONTACT_LOAD });
        return sdk.getUserContactData();
      })
      .then(apiSuccess<GetUserContactDataResponse>(dispatch, USER_CONTACT_SUCCESS))
      .catch(apiError(dispatch, USER_CONTACT_ERROR));

export type ServicePointListParams = {
  operators: ValueOf<typeof SERVICE_POINT_OPERATOR>[];
  canDeliverTo: boolean;
  codPossible: boolean | 'any';
};

export type NearbyServicePointListParams = {
  canDeliverTo: boolean;
  codPossible: boolean | 'any';
  latitude: number;
  longitude: number;
  operator: ['POCZTA' | 'INPOST'];
  limit: number;
};

export const getServicePointList =
  (
    dispatch: Dispatcher<
      { operator: string; servicePointsMap: Map<unknown, unknown> } | APIHTTPError | Error
    >,
  ) =>
    ({ operators, canDeliverTo, codPossible }: ServicePointListParams) =>
      deferredSDKFactory()
        .then(sdk => {
          dispatch({ type: SERVICE_POINTS_LOAD });
          const getOperatorServicePoints = withTimeout(sdk.getOperatorServicePoints.bind(sdk));

          operators.forEach(operator => {
            const operatorServicePoints = getOperatorServicePoints({
              operator,
              'filter.canDeliverTo': String(canDeliverTo),
              'filter.codPossible': String(codPossible),
            });

            return operatorServicePoints
              .then(servicePoints => {
                const servicePointsMap = new Map();
                servicePoints.data.forEach((point: ServicePointState) =>
                  servicePointsMap.set(point.id, point),
                );

                return { operator, servicePointsMap };
              })
              .then(apiSuccess(dispatch, SERVICE_POINTS_SUCCESS));
          });
        })
        .catch(apiError(dispatch, SERVICE_POINTS_ERROR));

export const getNearbyServicePointsList =
  (dispatch: Dispatcher<GetNearByServicePointsResponse | APIHTTPError | Error>) =>
    ({
      canDeliverTo,
      codPossible,
      latitude,
      longitude,
      operator,
      limit,
    }: NearbyServicePointListParams) =>
      deferredSDKFactory()
        .then(sdk => {
          dispatch({ type: SERVICE_POINTS_LOAD });
          const getServicePointsWithTimeout = withTimeout(sdk.getServicePoints.bind(sdk));
          return getServicePointsWithTimeout({
            'filter.canDeliverTo': String(canDeliverTo),
            'filter.codPossible': String(codPossible),
            latitude: String(latitude),
            longitude: String(longitude),
            limit: String(limit),
            ...(operator[0] ? { operator: operator[0] } : {}),
          });
        })
        .then(apiSuccess(dispatch, NEARBY_SERVICE_POINTS_SUCCESS))
        .catch(apiError(dispatch, NEARBY_SERVICE_POINTS_ERROR));

export const getServicePoint =
  (dispatch: Dispatcher<GetServicePointResponse | APIHTTPError | Error>) => (pointId: string) =>
    deferredSDKFactory()
      .then(sdk => {
        dispatch({ type: SERVICE_POINT_LOAD });
        const getServicePointWithTimeout = withTimeout(sdk.getServicePoint.bind(sdk));
        return getServicePointWithTimeout({
          pointId,
        }) as Promise<GetServicePointResponse>;
      })
      .then(response => {
        apiSuccess(dispatch, SERVICE_POINT_SUCCESS)(response);

        return response;
      })
      .catch(err => {
        apiError(dispatch, SERVICE_POINT_ERROR)(err);

        return err;
      });

export const getDeliveryPricePreview =
  (dispatch: Dispatcher<OrderPreviewPriceResponseV4 | APIHTTPError | Error>) =>
    (offerId: string, itemPrice: number) =>
      deferredSDKFactory()
        .then(sdk => {
          dispatch({ type: PRICE_PREVIEW_LOAD });
          const getOrderPreviewPrice = withTimeout(sdk.getOrderPreviewPricev4.bind(sdk));

          return getOrderPreviewPrice({ offerId, itemPrice: String(itemPrice) });
        })
        .then(apiSuccess<OrderPreviewPriceResponseV4>(dispatch, PRICE_PREVIEW_SUCCESS))
        .catch(apiError(dispatch, PRICE_PREVIEW_ERROR));

export const getShipmentMethods = (dispatch: Dispatcher<unknown>) => (offerId: string) => {
  dispatch({ type: SHIPMENT_METHODS_LOAD });
  const deliveriesSDK = deferredSDKFactory();
  const operators: Array<string> = [];

  // TODO: Add catch
  deliveriesSDK.then(sdk => {
    const getOfferShipmentMethodsWT = withTimeout(sdk.getOfferShipmentMethods.bind(sdk));
    getOfferShipmentMethodsWT({ offerId }).then((res: { data: any[] }) => {
      res.data.forEach(shipmentMethod => {
        operators.push(shipmentMethod.attributes.operator);
      });

      dispatch({ type: SHIPMENT_METHODS_SUCCESS, payload: res.data });
      dispatch({ type: OPERATORS_SUCCESS, payload: operators });
    });
  });
};

const getDeliveryOfferId = (atlasAd: AtlasAdResponse['data']): string => {
  const activeOffer = atlasAd?.delivery?.rock?.active;
  const offerId = atlasAd?.delivery?.rock?.offer_id;

  if (!offerId) {
    throw new Error('dlv_error_ad_without_delivery');
  }

  if (!activeOffer) {
    throw new Error('dlv_error_ad_without_delivery');
  }

  return offerId;
};

export const setPrepopulatedConsentChecked =
  (dispatch: Dispatcher<unknown>) => (checked: boolean) =>
    dispatch({ type: SET_PREPOPULATED_DATA_CONSENT_CHECKED, payload: checked });

export const getPrepopulatedUserData =
  (dispatch: Dispatcher<unknown>) =>
    (operators: string, trackReturningBuyer: () => void, prefillConsentSummaryEnabled: boolean) =>
      deferredSDKFactory()
        .then(sdk => {
          dispatch({ type: PREPOPULATED_DATA_LOAD });

          const getLegacyPrefillData = () =>
            sdk.getSuggestedCheckoutData({ operators }).then(({ data }) => {
            // TODO: DO-3088 Clean Up - Legacy Prefill
              if (data.suggestedCheckoutData?.length > 0) {
                dispatch({
                  type: SET_PREPOPULATED_DATA_IS_LEGACY,
                  payload: true,
                });
                return data.suggestedCheckoutData[0];
              }

              return null;
            });

          const getReusableData = () =>
            sdk
              .getReusableDataV2({ operators, context: USER_ROLE.BUYER })
              .then(({ data }) => {
                if (data.id) {
                // TODO: DO-3088 Clean Up - Legacy Prefill
                  dispatch({
                    type: SET_PREPOPULATED_DATA_IS_LEGACY,
                    payload: false,
                  });
                  return data;
                }
                return null;
              })
              .catch(error => {
                if (error.status === 404) {
                  return null;
                }
                throw error;
              });

          const prefillProvider = prefillConsentSummaryEnabled
            ? getReusableData
            : getLegacyPrefillData;

          return prefillProvider()
            .then(prefill => {
              if (prefill === null) return getLegacyPrefillData();
              return prefill;
            })
            .then(prefill => {
              trackReturningBuyer();
              let pointId = null;
              if ('operatorPoints' in prefill) {
                pointId = prefill.operatorPoints?.[0]?.value;
              } else if ('servicePointId' in prefill) {
              // Legacy Prefill Data
                pointId = prefill.servicePointId;
              }

              if (!pointId) return prefill;

              return sdk
                .getServicePoint({ pointId })
                .then(({ data }) => ({
                  ...prefill,
                  servicePoint: data,
                }))
                .catch(error => {
                  if (error.status === 404) {
                    return prefill;
                  }
                  throw error;
                });
            });
        })
        .then(apiSuccess(dispatch, PREPOPULATED_DATA_SUCCESS))
        .catch(error => {
          if (error.status === 404) {
            dispatch({ type: PREPOPULATED_DATA_ERROR, payload: error });
            return;
          }
          apiError(dispatch, PREPOPULATED_DATA_ERROR)(error);
        });

export const getAd = (dispatch: Dispatcher<unknown>) => (adId: string) => {
  const atlasSDK: AtlasSDK = atlasSDKFactory('v2');
  dispatch({ type: ATLAS_AD_LOAD });

  atlasSDK
    .getAd(adId)
    .then(({ data }: AtlasAdResponse) => {
      dispatch({
        type: SET_OFFER,
        payload: getDeliveryOfferId(data),
      });
      return { ...data };
    })
    .then(apiSuccess(dispatch, ATLAS_AD_SUCCESS))
    .catch(apiError(dispatch, ATLAS_AD_ERROR));
};

const processAtlasAd = (atlasAd: AtlasAdResponse['data']) => {
  const imageQuality = connectionQuality('512x512', '128x128');
  const photos = atlasAd?.photos?.map((photo: AdPhoto) => ({
    ...photo,
    link: photo?.link?.replace('{width}x{height}', imageQuality),
  }));

  const priceInCents = atlasAd?.params?.find(({ key }) => key === 'price').value?.value * 100;
  return { ...atlasAd, photos, priceInCents };
};

export const getOrderConfirmationInitialData =
  (dispatch: Dispatcher<unknown>) => (adId: string) => {
    const atlasSDK: AtlasSDK = atlasSDKFactory('v2');
    getContactData(dispatch)();
    dispatch({ type: ATLAS_AD_LOAD });
    atlasSDK
      .getAd(adId)
      .then(({ data }: AtlasAdResponse) => processAtlasAd(data))
      .then(apiSuccess(dispatch, ATLAS_AD_SUCCESS))
      .catch(apiError(dispatch, ATLAS_AD_ERROR));
  };

export const getCountries = (dispatch: Dispatcher<unknown>) => () => {
  clearAtlasSDKInstance();
  const atlasSDK: AtlasSDK = atlasSDKFactory('v1');
  dispatch({ type: ATLAS_COUNTRIES_LOAD });

  const changeAtlasKeys = (atlasCountries?: AtlasCountry[]): Country[] => {
    if (!atlasCountries?.length) return [];
    return atlasCountries.reduce(
      (countries: Country[], country: AtlasCountry) => [
        ...countries,
        {
          id: country.code,
          label: country.name,
        },
      ],
      [],
    );
  };

  atlasSDK
    .getCountries()
    .then(({ data }: Countries) =>
      dispatch({
        type: ATLAS_COUNTRIES_SUCCESS,
        payload: changeAtlasKeys(data?.countries),
      }),
    )
    .catch(apiError(dispatch, ATLAS_COUNTRIES_ERROR));
};

export const getCheckoutInitialData = (dispatch: Dispatcher<unknown>) => (adId: string) => {
  clearAtlasSDKInstance();
  const atlasSDK: AtlasSDK = atlasSDKFactory('v2');

  getContactData(dispatch)();
  dispatch({ type: ATLAS_AD_LOAD });
  atlasSDK
    .getAd(adId)
    .then(({ data }: AtlasAdResponse) => {
      const offerId = getDeliveryOfferId(data);
      const { value } = data.params.find(({ key }: { key: string }) => key === 'price');
      const price = Number((value.value * 100).toFixed());
      dispatch({ type: SET_OFFER, payload: offerId });
      getDeliveryPricePreview(dispatch)(offerId, price); // API needs value in cents
      getShipmentMethods(dispatch)(offerId);
      return processAtlasAd(data);
    })
    .then(apiSuccess(dispatch, ATLAS_AD_SUCCESS))
    .catch(apiError(dispatch, ATLAS_AD_ERROR));
};

const postUserAddress = (
  sdk: DeliverySDK,
  address: AddressResource,
): Promise<PostUserAddressResponse> => {
  const phone = address.attributes.phoneNumber.replace(/ /g, '');
  const validPhone = /^\+48/.test(phone) ? phone : `+48${phone}`;
  const postUserAddressWithTimeout = withTimeout(sdk.postUserAddress.bind(sdk));
  return postUserAddressWithTimeout({
    data: {
      attributes: {
        ...address.attributes,
        phoneNumber: validPhone,
      },
    },
  });
};

export const postOrder =
  (dispatch: Dispatcher<PostOrderResponse | Error | APIHTTPError | ErrorReason>) =>
    (
      adId: string,
      offerId: string,
      address: AddressResource,
      destinationStationId: string,
      paymentMethod: GetOfferShipmentMethodResponse['paymentMethod'],
      invoice: PostOrderMarketpayRequestV3['data']['invoice'],
      sessionLong: string,
      isPrefillDataReusable: boolean,
      priceComponents: PriceComponent[],
      areDonationsEnabled: boolean,
      loyaltyPointsOptInCheck: boolean,
    ) =>
      deferredSDKFactory()
      // TODO: Get rid of any type. Need it cause FE pipeline need to have API ready for prod also to get success build
        .then((sdk: any) => {
          dispatch({ type: POST_ORDER_LOAD });

          const isMarketpay = paymentMethod === 'marketpay';
          const returnUrl = `https://${sites.delivery.domain}${deliveryPaths.checkout}/${adId}/success`;

          if (isMarketpay) {
            const { attributes } = address;
            const validPhoneNumber = /^\+48/.test(attributes.phoneNumber)
              ? attributes.phoneNumber
              : `${phonePrefix}${attributes.phoneNumber}`;

            const orderPayload: PostOrderMarketpayRequestV3 = {
              data: {
                address: {
                  postCode: attributes.postCode,
                  city: attributes.city,
                  streetName: attributes.streetName,
                  streetNumber: attributes.streetNumber,
                  apartmentNumber: attributes.apartmentNumber,
                },
                contact: {
                  firstName: attributes.firstName,
                  lastName: attributes.lastName,
                  companyName: attributes.companyName,
                  email: attributes.email,
                  phoneNumber: validPhoneNumber,
                },
                offerId,
                servicePointId: destinationStationId,
                returnUrl,
                invoice,
                isReusable: isPrefillDataReusable,
                ...(areDonationsEnabled && { priceComponents }),
                loyaltyProgram: {
                  doOptIn: loyaltyPointsOptInCheck,
                },
              },
            };

            return sdk.postMarketpayOrderV3(orderPayload);
          }

          return postUserAddress(sdk, address).then(({ data }) => {
            const payload = {
              data: {
                attributes: {
                  offerId,
                  deliveryAddressId: data.id,
                  destinationStationId,
                },
              },
            };
            if (isMarketpay) {
              return sdk.postMarketpayOrder(
                {
                  data: {
                    attributes: {
                      ...payload.data.attributes,
                      returnUrl,
                    },
                  },
                },
                {},
                'marketpay-order',
              );
            }

            return sdk.postOrder(payload, {}, 'order');
          });
        })
        .then(apiSuccess(dispatch, POST_ORDER_SUCCESS))
        .catch(
          (
            err:
            | {
                error: boolean;
                status: string;
                body: PostOrderConflictError;
              }
            | Error
            | APIHTTPError,
          ): void => {
            const isAPIError = err instanceof Error || !err.body || !Array.isArray(err.body.errors);
            if (isAPIError) apiError(dispatch, POST_ORDER_ERROR)(err as APIHTTPError);
            else if (err.error && err.body) {
              const getFieldFromSource = (source: any) => source?.pointer?.split('/').pop();

              err.body.errors.forEach((validation: ErrorReason) =>
                dispatch({
                  type: POST_ORDER_ERROR_(validation.code || validation.reason),
                  payload: validation?.field
                    ? validation
                    : {
                      ...validation,
                      field: getFieldFromSource(validation.source),
                    },
                }),
              );
            }
            return null;
          },
        );

/**
 * TODO: After migrating to v2 API (marketpay) this handler will be no longer necessary
 * as it resposability is basically to handle equally the responses and errors
 * of both v1 and v2 APIs.
 */
const postContractResponseHandler = (
  dispatch: Dispatcher<PostContractResponse | number | APIHTTPError | Error | ErrorReason>,
  response: Promise<PostContractResponse | number | APIHTTPError | Error>,
) =>
  response.then(apiSuccess(dispatch, POST_CONTRACT_SUCCESS)).catch(
    (
      err:
        | {
            error: boolean;
            status: string;
            body: PostContractValidationErrors;
          }
        | APIHTTPError
        | Error,
    ): void => {
      const isAPIError = err instanceof Error || !err.body || !Array.isArray(err.body.errors);
      if (isAPIError) apiError(dispatch, POST_ORDER_ERROR)(err as APIHTTPError);
      else if (err.error && err.body) {
        err.body.errors.forEach((validation: ErrorReason) => {
          if (validation.reason === API_ERROR_CODES.POSTCODE_NOT_SUPPORTED) {
            dispatch({ type: UNSET_USE_DELIVERY_DETAILS_FOR_INVOICE_FORM });
          }
          dispatch({
            type: POST_CONTRACT_ERROR_(validation.code || validation.reason),
            payload: validation,
          });
        });
      }
      return null;
    },
  );

export const postContract =
  (dispatch: Dispatcher<PostContractResponse | number | Error>) =>
    (
      orderId: string,
      address: Omit<AddressResource, 'type'>,
      sellerIban: string,
      postingStationId: string,
      keepOlxDelivery?: boolean,
    ) =>
      postContractResponseHandler(
        dispatch,
        deferredSDKFactory().then(sdk => {
          dispatch({ type: POST_CONTRACT_LOAD });
          return postUserAddress(sdk, address).then(({ data }) =>
            sdk.postContract(
              {
                data: {
                  attributes: {
                    orderId,
                    returnAddressId: data.id,
                    sellerIban,
                    postingStationId,
                    keepOlxDelivery,
                  },
                },
              },
              {},
              'contract',
            ),
          );
        }),
      );

export const postMarketPayContract =
  (dispatch: Dispatcher<PostContractResponse | Error>) =>
    (
      orderId: string,
      address: Omit<AddressResource, 'type'>,
      postingStationId: string,
      postingMethod?: PostingMethodTypes | '',
      keepOlxDelivery?: boolean,
      wantsInvoiceFor?: InvoiceFor,
    ) =>
      postContractResponseHandler(
        dispatch,
        deferredSDKFactory().then(sdk => {
          dispatch({ type: POST_CONTRACT_LOAD });
          const returnUrl = `https://${sites.delivery.domain}${deliveryPaths.sellerConfirmation}/success/${orderId}`;

          return postUserAddress(sdk, address).then(({ data }) =>
            sdk.postMarketpayContract(
              {
                data: {
                  attributes: {
                    orderId,
                    returnAddressId: data.id,
                    postingStationId,
                    returnUrl,
                    postingMethod,
                    keepOlxDelivery,
                    wantsInvoiceFor,
                  },
                },
              },
              {},
              'marketpay-contract',
            ),
          );
        }),
      );

export const postBillingData =
  (dispatch: Dispatcher<APIHTTPError | Error | ErrorReason | unknown>) =>
    (orderID: string, billingAddressResource: BillingAddressResource, userType?: string) =>
      deferredSDKFactory()
        .then(sdk => {
          dispatch({ type: POST_CONTRACT_LOAD });

          return sdk.postBillingData({
            data: { ...billingAddressResource },
            orderID,
            userType,
          });
        })
        .then(apiSuccess(dispatch, POST_CONTRACT_SUCCESS))
        .catch((err: APIHTTPError | Error): void => {
          const isAPIError = err instanceof Error || !err.body || !Array.isArray(err.body.errors);
          if (isAPIError) apiError(dispatch, POST_ORDER_ERROR)(err as APIHTTPError);
          else if (err.error && err.body) {
            err.body.errors.forEach((validation: ErrorReason) => {
              if (validation.reason) {
                dispatch({ type: UNSET_USE_DELIVERY_DETAILS_FOR_INVOICE_FORM });
                dispatch({
                  type: POST_BILLING_ERROR_(validation.reason),
                  payload: validation,
                });
              }
            });
          }
          throw new Error('Could not save billing data.');
        });

export const getSellerDetails =
  (dispatch: Dispatcher<GetSellerResponseV1 | boolean | unknown>) => (offerId: string) => {
    dispatch({ type: LOADING_SELLER_DETAILS, payload: true });
    return deferredSDKFactory().then(sdk => {
      sdk
        .getSellerByOfferId({ offerId })
        .then(({ data = null }) => {
          dispatch({ type: GET_SELLER_DETAILS, payload: data });
        })
        .catch(() => {
          dispatch({ type: LOADING_SELLER_DETAILS, payload: false });
        });
    });
  };
export const getLoyaltyPointsFlagHubVisible =
  (
    dispatch: Dispatcher<
      | UnifiedPlatformEndpointsResponses[UnifiedPlatformEndpoints.loyaltyPointsFlagHubVisible]
      | APIHTTPError
      | Error
    >,
  ) =>
    (userId: string) => {
      dispatch({ type: LOYALTY_POINTS_FLAG_HUB_VISIBLE_LOAD });
      return fetchUnifiedPlatformAPI({
        endpoint: `${UnifiedPlatformEndpoints.loyaltyPointsFlagHubVisible}/${userId}`,
      })
        .then(res => {
          if (res.status === 200) {
            return dispatch({
              type: LOYALTY_POINTS_FLAG_HUB_VISIBLE_SUCCESS,
              payload: 'VISIBLE',
            });
          }
          if (res.status === 404) {
            return dispatch({
              type: LOYALTY_POINTS_FLAG_HUB_VISIBLE_SUCCESS,
              payload: 'NOT_VISIBLE',
            });
          }
          throw new Error('Loyalty Points - Flag Hub Visible - Unexpected response');
        })
        .catch(apiError(dispatch, LOYALTY_POINTS_FLAG_HUB_VISIBLE_ERROR));
    };

export const getLoyaltyPointsOptInStatus =
  (
    dispatch: Dispatcher<
      | UnifiedPlatformEndpointsResponses[UnifiedPlatformEndpoints.loyaltyPointsOptInStatus]
      | APIHTTPError
      | Error
    >,
  ) =>
    () => {
      dispatch({ type: LOYALTY_POINTS_OPT_IN_STATUS_LOAD });
      return fetchUnifiedPlatformAPI({ endpoint: UnifiedPlatformEndpoints.loyaltyPointsOptInStatus })
        .then(res => {
          if (res.status !== 200) {
            throw new Error('Loyalty Points - Opt In Status - Unexpected response');
          }
          return res.json();
        })
        .then(apiSuccess(dispatch, LOYALTY_POINTS_OPT_IN_STATUS_SUCCESS))
        .catch(apiError(dispatch, LOYALTY_POINTS_OPT_IN_STATUS_ERROR));
    };

export const postLoyaltyPointsPreviewPoints =
  (
    dispatch: Dispatcher<
      | UnifiedPlatformEndpointsResponses[UnifiedPlatformEndpoints.loyaltyPointsPreviewPoints]
      | APIHTTPError
      | Error
    >,
  ) =>
    (itemPrice: number) => {
      dispatch({ type: LOYALTY_POINTS_PREVIEW_POINTS_LOAD });
      return fetchUnifiedPlatformAPI({
        endpoint: UnifiedPlatformEndpoints.loyaltyPointsPreviewPoints,
        method: 'POST',
        body: JSON.stringify({
          priceOfOrderInCents: itemPrice,
        }),
      })
        .then(res => {
          if (res.status !== 200) {
            throw new Error('Loyalty Points - Preview - Unexpected response');
          }
          return res.json();
        })
        .then(apiSuccess(dispatch, LOYALTY_POINTS_PREVIEW_POINTS_SUCCESS))
        .catch(apiError(dispatch, LOYALTY_POINTS_PREVIEW_POINTS_ERROR));
    };

export const postLoyaltyPointsWhitelistUser =
  (
    dispatch: Dispatcher<
      | UnifiedPlatformEndpointsResponses[UnifiedPlatformEndpoints.loyaltyPointsWhitelistUser]
      | APIHTTPError
      | Error
    >,
  ) =>
    () => {
      dispatch({ type: LOYALTY_POINTS_WHITELIST_USER_LOAD, payload: 'NOT_WHITELISTED' });
      return fetchUnifiedPlatformAPI({
        endpoint: UnifiedPlatformEndpoints.loyaltyPointsWhitelistUser,
        method: 'POST',
        body: JSON.stringify({}),
      })
        .then(res => {
          if (res.status === 201 || res.status === 409) {
            return dispatch({
              type: LOYALTY_POINTS_WHITELIST_USER_SUCCESS,
              payload: 'WHITELISTED',
            });
          }
          throw new Error('Loyalty Points - Whitelist - User - Unexpected response');
        })
        .catch(apiError(dispatch, LOYALTY_POINTS_WHITELIST_USER_ERROR));
    };

export const setLoyaltyPointsOptInCheck = (dispatch: Dispatcher<boolean>) => () => {
  dispatch({ type: LOYALTY_POINTS_OPT_IN_CHECK_TOGGLE });
};
